import { Link } from 'react-router-dom';
import { useCategories } from '@/hooks/useCategories';
import { Loader2 } from 'lucide-react';
import { AnimateOnScroll } from './AnimateOnScroll';

export function CategoryGrid() {
  const { data: categories, isLoading } = useCategories();

  if (isLoading) {
    return (
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <AnimateOnScroll animation="fade-in-up" className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            הקטגוריות שלנו
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            בחרו את הקטגוריה המועדפת עליכם
          </p>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          {categories?.map((category, index) => (
            <AnimateOnScroll
              key={category.id}
              animation="scale-in"
              delay={index * 100}
            >
              <Link
                to={category.slug === 'all' ? '/meals' : `/meals?category=${category.slug}`}
                className="group relative aspect-[4/5] overflow-hidden rounded-3xl shadow-xl block min-h-[280px] md:min-h-[320px]"
              >
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
                  style={{
                    backgroundImage: category.image_url
                      ? `url(${category.image_url})`
                      : 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--accent)) 100%)',
                  }}
                />
                
                {/* Overlay with Gradient Animation */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-all duration-500 group-hover:from-black/90 group-hover:via-black/50" />
                
                {/* Shine Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
                </div>
                
                {/* Glow Effect on Hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-primary/20 to-transparent" />
                
                {/* Content */}
                <div className="absolute inset-0 flex items-end p-4">
                  <div className="w-full">
                    <h3 className="text-white text-xl md:text-2xl font-bold text-center transform transition-all duration-500 group-hover:-translate-y-3 group-hover:scale-105">
                      {category.name_he}
                    </h3>
                    
                    {/* Animated Underline */}
                    <div className="h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mt-2 w-0 group-hover:w-20 transition-all duration-500 ease-out" />
                    
                    {/* Explore Text */}
                    <p className="text-white/0 group-hover:text-white/80 text-sm text-center mt-2 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                      לחצו לצפייה
                    </p>
                  </div>
                </div>
                
                {/* Border Glow Animation */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-accent/60 transition-all duration-500 group-hover:shadow-[inset_0_0_20px_rgba(var(--accent),0.3)]" />
              </Link>
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}
